import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';

import {
	CreateTableNew as CreateTable,
	SearchBar,
	Button,
	GeneralMessage,
	useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import {
	Input,
	DateInput,
	NumberInput
} from '../../common/Input';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';

function DevTest({ user, isHideSideMenu }) {
	// Use the hook for message management
	const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

	var headers = [
		"ID",
		"Title",
		"Description",
		"Goal",
		"Year Count",
		"Start Date",
		"End Date",
		"Status",
		"Actions"
	]

	var accessors = [
		"id",
		"title",
		"description",
		"goal",
		"yearCount",
		"startDate",
		"endDate",
		"isActive",
		"actions"
	]

	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [loadingRequest, setLoadingRequest] = useState(false);
	const [data, setData] = useState([]);
	const [selectedData, setSelectedData] = useState(null);

	// Form state
	const [form, setForm] = useState({
		title: "",
		description: "",
		goal: "",
		yearCount: "",
		startDate: "",
		endDate: "",
		isActive: true,
	});

	const resetLocalVariables = () => {
		setForm({
			title: "",
			description: "",
			goal: "",
			yearCount: 0,
			startDate: "",
			endDate: "",
			isActive: true,
		});
		setSelectedData(null);
	}

	// Retrieve
	const [filterData, setFilterData] = useState([]);
	// Add
	const [showAddForm, setShowAddForm] = useState(false);
	// Update
	const [showEditForm, setShowEditForm] = useState(false);

	const apiUrl = domain + "/api/DevTest";

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const response = await axios.get(apiUrl, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			})
			setData(response.data);
			setFilterData(response.data);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	}, [apiUrl]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleFilter = (value) => {
		setCurrentPage(1);
		const lowercaseValue = value.toLowerCase();

		if (!value) {
			// If input is empty, reset to original list
			setData(filterData);
			return;
		}

		const res = filterData.filter((f) => {
			const match1 = f.title?.toString().toLowerCase().includes(lowercaseValue);
			const match2 = f.description?.toString().toLowerCase().includes(lowercaseValue);
			const match3 = f.goal?.toString().toLowerCase().includes(lowercaseValue);
			const match4 = f.id?.toString().startsWith(lowercaseValue);
			return match1 || match2 || match3 || match4;
		});

		setData(res);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		// Prepare data for API
		const apiData = {
			title: form.title || "",
			description: form.description || "",
			goal: form.goal || "",
			yearCount: form.yearCount || 0,
			startDate: form.startDate || "",
			endDate: form.endDate || "",
			isActive: form.isActive !== undefined ? form.isActive : true,
		};

		console.log('Add Submitting data:', apiData);

		try {
			setLoadingRequest(true);
			await axios.post(apiUrl, apiData, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			// Audit trail logic from original
			await SetAuditTrail({
				setupArea: "DevTest",
				actionTaken: "insert",
				field: "Add",
				newValue: `${apiData.title}`,
				oldValue: "-",
				dateSaved: new Date(),
				savedById: user.id
			});

			await fetchData();

			showGeneralMessage("DevTest created successfully!", true);
			closeAddHandleClick();
		} catch (error) {
			console.error('Error creating data:', error.response || error);

			const errorMessage = error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.message ||
				"Failed to create DevTest";
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleUpdate = async (event) => {
		event.preventDefault();

		if (!selectedData?.id) {
			showGeneralMessage("No record selected for update", false);
			return;
		}

		const apiData = {
			title: form.title || "",
			description: form.description || "",
			goal: form.goal || "",
			yearCount: form.yearCount || 0,
			startDate: form.startDate || "",
			endDate: form.endDate || "",
			isActive: form.isActive !== undefined ? form.isActive : true,
		};

		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			// Audit trail logic from original
			let auditInfo = [];

			if (apiData.title !== selectedData.title) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "Title",
					newValue: `${apiData.title}`,
					oldValue: `${selectedData.title}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.description !== selectedData.description) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "Description",
					newValue: `${apiData.description}`,
					oldValue: `${selectedData.description}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.goal !== selectedData.goal) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "Goal",
					newValue: `${apiData.goal}`,
					oldValue: `${selectedData.goal}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.yearCount !== selectedData.yearCount) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "Year Count",
					newValue: `${apiData.yearCount}`,
					oldValue: `${selectedData.yearCount}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.startDate !== selectedData.startDate) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "Start Date",
					newValue: `${apiData.startDate}`,
					oldValue: `${selectedData.startDate}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.endDate !== selectedData.endDate) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "End Date",
					newValue: `${apiData.endDate}`,
					oldValue: `${selectedData.endDate}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.isActive !== selectedData.isActive) {
				auditInfo.push({
					setupArea: "DevTest",
					actionTaken: "update",
					field: "Is Active?",
					newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
					oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (auditInfo.length > 0) {
				await SetAuditTrailBulk({ data: auditInfo });
			}

			setShowEditForm(false);
			await fetchData();

			showGeneralMessage("DevTest updated successfully!", true);
			closeEditHandleClick();
		} catch (error) {
			console.error('Error updating data:', error.response || error);

			const errorMessage = error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.message ||
				"Failed to update DevTest";
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const editHandleClick = (data) => {
		setSelectedData(data);

		setForm({
			title: data.title || "",
			description: data.description || "",
			goal: data.goal || "",
			yearCount: data.yearCount || 0,
			startDate: data.startDate || "",
			endDate: data.endDate || "",
			isActive: data.isActive !== undefined ? data.isActive : true,
		});
		openEditHandleClick();
	};

	const openAddHandleClick = () => {
		setShowAddForm(true);
	};

	const closeAddHandleClick = () => {
		setShowAddForm(false);
		resetLocalVariables();
	};

	const openEditHandleClick = () => {
		setShowEditForm(true);
	};

	const closeEditHandleClick = () => {
		setShowEditForm(false);
		resetLocalVariables();
	};

	const actions = [
		{
			name: "Edit",
			tooltip: "edit",
			icon: <EditIcon fontSize="small" />,
			className: "bg-green-500 hover:bg-green-600 flex items-center",
			function: editHandleClick,
			visible: true,
		},
	];

	return (
		<div>
			<div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
				<div className="LeftSpace"></div>
				<div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
					<div className="MainteInfo shadow-xl">
						<div className="mainteHeader">
							<div className='sub-title row'>
								<span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Dev Mode >"} </span>
								<span style={{ borderBottom: "1px solid black" }}>{"DevTest"}</span>
							</div>
							<h3>DevTest</h3>
						</div>
						<div className='p-4 space-y-4'>
							<SearchBar handleFilter={handleFilter} />

							<Button icon={<AddIcon />}
								name={"Add DevTest"}
								className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
								onClick={openAddHandleClick}
							/>
							<CreateTable
								tableName={"DevTests"}
								headers={headers}
								accessors={accessors}
								data={data}
								actions={actions}
								currentPage={currentPage}
								setCurrentPage={setCurrentPage}
								itemsPerPage={10}
								totalItems={data?.length}
							/>
						</div>
					</div>
				</div>
			</div>
			<AddEditForm
				isOpen={showAddForm}
				onClose={closeAddHandleClick}
				form={form}
				setForm={setForm}
				handleSubmit={handleSubmit}
				modalName={"Add DevTest"}
				mode="add"
				showGeneralMessage={showGeneralMessage}
			/>
			<AddEditForm
				isOpen={showEditForm}
				onClose={closeEditHandleClick}
				form={form}
				setForm={setForm}
				handleSubmit={handleUpdate}
				modalName={"Update DevTest"}
				mode="edit"
				showGeneralMessage={showGeneralMessage}
			/>

			{/* Use the reusable GeneralMessage component */}
			<GeneralMessage
				form={showMessage}
				message={message}
				isSuccess={isSuccess}
			/>

			<LoadingSpinner loading={loadingRequest || loading} />

		</div>
	)
}

function AddEditForm({
	isOpen,
	modalName,
	onClose,
	form,
	setForm,
	handleSubmit,
	mode = "add", // "add", "edit",
	showGeneralMessage
}) {
	const [errorState, setErrorState] = useState({
		title: false,
		description: false,
		goal: false,
		yearCount: false,
		startDate: false,
		endDate: false
	});

	const submit = (e) => {
		e.preventDefault();

		// Validation
		const newErrorState = {
			title: !form.title,
			description: !form.description,
			goal: !form.goal,
			yearCount:
				form.yearCount === "" ||
				isNaN(Number(form.yearCount)) ||
				Number(form.yearCount) < 0,
			startDate: !form.startDate,
			endDate: !form.endDate
		};

		setErrorState(newErrorState);

		// Check if any error exists
		const hasError = Object.values(newErrorState).some(error => error);
		if (hasError) {
			return;
		}

		// Check if end date is after start date
		if (new Date(form.endDate) < new Date(form.startDate)) {
			showGeneralMessage("End date must be after start date", false);
			return;
		}

		handleSubmit(e);
	};

	const handleClose = () => {
		onClose();
		setErrorState({
			title: false,
			description: false,
			goal: false,
			yearCount: false,
			startDate: false,
			endDate: false
		});
	};

	const handleInputChange = (field, value) => {
		setForm(prev => ({
			...prev,
			[field]: value
		}));

		// Clear error for this field when user starts typing
		if (errorState[field]) {
			setErrorState(prev => ({
				...prev,
				[field]: false
			}));
		}
	};

	// Handler for date input
	const handleDateChange = (field, e) => {
		handleInputChange(field, e.target.value);
	};

	if (!isOpen) return null;

	const isEditMode = mode === "edit";

	return (
		<>
			<div
				role="dialog"
				aria-modal="true"
				className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
			>
				<div className="bg-gray-50 w-full md:w-[90%] lg:w-[60%] xl:w-[50%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

					{/* Header */}
					<div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
						<h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
							<span className="text-xl text-gray-600">{modalName}</span>
						</h2>

						<button
							onClick={handleClose}
							className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
							aria-label="Close modal"
						>
							<CloseIcon fontSize="medium" />
						</button>
					</div>

					{/* Body */}
					<div className="p-6 overflow-y-auto flex-grow space-y-4">

						{/* DevTest Form */}
						<div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
							<div className="py-1 border-b border-gray-300">
								<h3 className="font-medium text-gray-700">DevTest Information</h3>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-3 pt-3">
								<Input
									label="Title:"
									value={form.title}
									onChange={(e) => handleInputChange('title', e.target.value)}
									placeholder="Enter title"
									isRequired={true}
									error={errorState.title}
									errorMessage="Please fill up this field"
								/>

								<NumberInput
									label="Year Count:"
									value={form.yearCount}
									onChange={(e) => handleInputChange("yearCount", e.target.value)}
									placeholder="Enter year count"
									isRequired={true}
									error={errorState.yearCount}
									errorMessage="Please enter a valid year count"
									min={0}
								/>

								<Input
									label="Description:"
									value={form.description}
									onChange={(e) => handleInputChange('description', e.target.value)}
									placeholder="Enter description"
									isRequired={true}
									error={errorState.description}
									errorMessage="Please fill up this field"
								/>

								<Input
									label="Goal:"
									value={form.goal}
									onChange={(e) => handleInputChange('goal', e.target.value)}
									placeholder="Enter goal"
									isRequired={true}
									error={errorState.goal}
									errorMessage="Please fill up this field"
								/>

								<DateInput
									label="Start Date:"
									value={form.startDate}
									onChange={(e) => handleDateChange('startDate', e)}
									placeholder="Select start date"
									isRequired={true}
									error={errorState.startDate}
									errorMessage="Please select a start date"
								/>

								<DateInput
									label="End Date:"
									value={form.endDate}
									onChange={(e) => handleDateChange('endDate', e)}
									placeholder="Select end date"
									isRequired={true}
									error={errorState.endDate}
									errorMessage="Please select an end date"
								/>

								<div className="flex items-center pt-2 md:col-span-2">
									<input
										type="checkbox"
										checked={form.isActive !== undefined ? form.isActive : true}
										onChange={(e) => handleInputChange('isActive', e.target.checked)}
										className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
									/>
									<label className="ml-2 text-sm font-medium text-gray-600">
										Active
									</label>
								</div>
							</div>
						</div>

					</div>

					{/* Footer */}
					<div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
						<button
							onClick={handleClose}
							className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
						>
							Cancel
						</button>

						<button
							onClick={submit}
							className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
						>
							{isEditMode ? "Update" : "Save"}
						</button>
					</div>

				</div>
			</div>
		</>
	);
}

export default DevTest